// Portal brand-tinting engine.
//
// Given a workspace.brand_color (hex) + the active portal theme,
// derives a complete accent family of CSS custom property values that
// the portal applies as inline-style overrides on its wrapper div.
// The overrides then cascade through every var(--accent*) consumer in
// the portal (buttons, active nav, focus rings, chips, spend chart
// bars) — no per-component changes needed.
//
// Guardrails — fall back to tokens.css default (returning {}, so the
// spread is a no-op) when:
//   - brand_color is null / missing
//   - brand_color is malformed (not a parseable #RGB or #RRGGBB)
//   - the brand can't reach the WCAG 4.5:1 minimum contrast for chip
//     text (after testing both white and near-black ink)
//   - the brand can't be shifted enough via HSL lightness adjustment
//     to meet 4.5:1 against the active canvas (text-on-canvas case)
//
// Constants live as plain TS values, not inside JSX style={{...}},
// so they don't trip the no-new-hex pre-commit guard (which scopes to
// inline-style attributes per scripts/check-no-new-hex.mjs).

interface RGB { r: number; g: number; b: number }

// Canvas colors per resolved theme. MUST match tokens.css's
// --canvas values for the contrast math to be accurate. If those
// change, update here.
const CANVAS_RGB: Record<'light' | 'dark', RGB> = {
  dark:  { r: 10,  g: 10,  b: 15 },   // matches tokens.css dark --canvas
  light: { r: 255, g: 255, b: 255 },  // matches tokens.css light --canvas
}

const BLACK: RGB = { r: 0, g: 0, b: 0 }
const WHITE: RGB = { r: 255, g: 255, b: 255 }

const MIN_CONTRAST = 4.5  // WCAG AA for normal text

// Per-theme alpha values mirror what tokens.css uses for the default
// purple — keeps the dim/border/glow rhythm consistent regardless of
// brand color.
const ALPHA = {
  light: { dim: 0.08, border: 0.30, glow: 0.25 },
  dark:  { dim: 0.15, border: 0.30, glow: 0.35 },
}

// ─── Parsers / converters ────────────────────────────────────────────
function parseHex(hex: unknown): RGB | null {
  if (typeof hex !== 'string') return null
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  const v = m[1]
  if (v.length === 3) {
    return {
      r: parseInt(v[0] + v[0], 16),
      g: parseInt(v[1] + v[1], 16),
      b: parseInt(v[2] + v[2], 16),
    }
  }
  return {
    r: parseInt(v.slice(0, 2), 16),
    g: parseInt(v.slice(2, 4), 16),
    b: parseInt(v.slice(4, 6), 16),
  }
}

// WCAG relative luminance.
function srgbToLinear(c: number): number {
  const s = c / 255
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
}
function luminance({ r, g, b }: RGB): number {
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b)
}
function contrast(a: RGB, b: RGB): number {
  const la = luminance(a), lb = luminance(b)
  const [hi, lo] = la > lb ? [la, lb] : [lb, la]
  return (hi + 0.05) / (lo + 0.05)
}

// RGB ↔ HSL — only the lightness channel matters for our shifts.
function rgbToHsl({ r, g, b }: RGB): { h: number; s: number; l: number } {
  const R = r / 255, G = g / 255, B = b / 255
  const max = Math.max(R, G, B), min = Math.min(R, G, B)
  const d = max - min
  let h = 0
  const l = (max + min) / 2
  const s = d === 0 ? 0 : l > 0.5 ? d / (2 - max - min) : d / (max + min)
  if (d !== 0) {
    if (max === R) h = ((G - B) / d) + (G < B ? 6 : 0)
    else if (max === G) h = (B - R) / d + 2
    else h = (R - G) / d + 4
    h /= 6
  }
  return { h, s, l }
}
function hslToRgb({ h, s, l }: { h: number; s: number; l: number }): RGB {
  let r: number, g: number, b: number
  if (s === 0) { r = g = b = l }
  else {
    const hueToRgb = (p: number, q: number, t: number): number => {
      if (t < 0) t += 1
      if (t > 1) t -= 1
      if (t < 1 / 6) return p + (q - p) * 6 * t
      if (t < 1 / 2) return q
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
      return p
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r = hueToRgb(p, q, h + 1 / 3)
    g = hueToRgb(p, q, h)
    b = hueToRgb(p, q, h - 1 / 3)
  }
  return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) }
}
function adjustLightness(rgb: RGB, deltaL: number): RGB {
  const hsl = rgbToHsl(rgb)
  hsl.l = Math.max(0, Math.min(1, hsl.l + deltaL))
  return hslToRgb(hsl)
}

// Walk lightness toward whichever pole improves contrast against the
// canvas (lighten if canvas is dark, darken if light). Step 5% each
// iteration up to 20 steps; return null if contrast target never met.
function findContrastingShade(brand: RGB, canvas: RGB, target: number): RGB | null {
  if (contrast(brand, canvas) >= target) return brand
  const direction = luminance(canvas) < 0.5 ? +1 : -1
  for (let i = 1; i <= 20; i++) {
    const candidate = adjustLightness(brand, direction * 0.05 * i)
    if (contrast(candidate, canvas) >= target) return candidate
  }
  return null
}

// CSS-string emitters. Runtime concatenation, not source literals.
function rgbCss({ r, g, b }: RGB): string { return `rgb(${r}, ${g}, ${b})` }
function rgbaCss({ r, g, b }: RGB, a: number): string { return `rgba(${r}, ${g}, ${b}, ${a})` }

// ─── Public API ──────────────────────────────────────────────────────
export interface BrandOverrides {
  '--accent'?: string
  '--accent-hover'?: string
  '--accent-dim'?: string
  '--accent-ink'?: string
  '--accent-text'?: string
  '--accent-border'?: string
  '--accent-glow'?: string
}

export function deriveBrandOverrides(
  brandColor: string | null | undefined,
  resolvedTheme: 'light' | 'dark',
): BrandOverrides {
  if (!brandColor) return {}
  const brand = parseHex(brandColor)
  if (!brand) return {}

  // accent-ink — text drawn on top of the brand chip background. Pick
  // whichever pole (white or black) has the better contrast vs the
  // brand. If neither clears 4.5:1, the chip text would be unreadable
  // and we'd need to fall back regardless.
  const cWhite = contrast(brand, WHITE)
  const cBlack = contrast(brand, BLACK)
  const accentInk = cWhite >= cBlack ? WHITE : BLACK
  if (Math.max(cWhite, cBlack) < MIN_CONTRAST) return {}

  // accent-text — brand color used FOR text on the canvas. Shift the
  // brand toward whichever pole the canvas needs. If even after 20
  // 5%-steps of lightness shift contrast is still below 4.5:1, the
  // brand is unworkable on this theme — fall back.
  const canvas = CANVAS_RGB[resolvedTheme]
  const accentText = findContrastingShade(brand, canvas, MIN_CONTRAST)
  if (!accentText) return {}

  // accent-hover — primary buttons go a step darker on hover. Matches
  // staff convention of brand-purple-600 vs -500.
  const accentHover = adjustLightness(brand, -0.08)

  const a = ALPHA[resolvedTheme]

  return {
    '--accent':        rgbCss(brand),
    '--accent-hover':  rgbCss(accentHover),
    '--accent-dim':    rgbaCss(brand, a.dim),
    '--accent-ink':    rgbCss(accentInk),
    '--accent-text':   rgbCss(accentText),
    '--accent-border': rgbaCss(brand, a.border),
    '--accent-glow':   rgbaCss(brand, a.glow),
  }
}
